import { useEffect, useState } from "react";
import { ShieldCheck, Sliders, Trash2, UserPlus, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, shortDate } from "../lib/api";

const ACCESS = [
  { key: "create_accounts", label: "Create client accounts", hint: "Approve setup requests and generate client logins" },
  { key: "manage_clients", label: "Open client accounts", hint: "View and switch into any client workspace" },
];

const PRESETS = {
  reviewer: [],
  account_manager: ["create_accounts"],
  full_access: ["create_accounts", "manage_clients"],
};

const permsToPreset = (perms) => {
  const set = perms.filter((p) => p !== "review_requests").sort().join(",");
  for (const [key, value] of Object.entries(PRESETS)) {
    if (value.slice().sort().join(",") === set) return key;
  }
  return "custom";
};

const accessLabel = (perms) => {
  const extra = (perms || []).filter((p) => p !== "review_requests");
  if (extra.length === 0) return "Reviewer";
  if (extra.includes("create_accounts") && extra.includes("manage_clients")) return "Full Access";
  if (extra.includes("create_accounts")) return "Account Manager";
  if (extra.includes("manage_clients")) return "Client Access";
  return "Custom";
};

export default function TeamView() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", perms: [] });
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .get("/team/members")
      .then(({ data }) => setMembers(data))
      .catch((error) => toast.error(error.response?.data?.detail || "Could not load your team"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const togglePerm = (key) => setForm((prev) => ({ ...prev, perms: prev.perms.includes(key) ? prev.perms.filter((p) => p !== key) : [...prev.perms, key] }));

  const invite = async (event) => {
    event.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/team/members", { name: form.name, email: form.email, password: form.password, permissions: [...form.perms, "review_requests"] });
      toast.success(`${form.name || form.email} can now sign in`);
      setForm({ name: "", email: "", password: "", perms: [] });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add this team member");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Remove ${member.name || member.email} from your team?`)) return;
    try {
      await api.delete(`/team/members/${member.id}`);
      toast.success("Team member removed");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove this member");
    }
  };

  return (
    <section className="team-view" data-testid="team-view">
      <div className="panel team-invite-panel">
        <div className="team-invite-heading">
          <div className="team-invite-icon"><UserPlus size={18} /></div>
          <div>
            <h3>Invite a team member</h3>
            <p>Every team member can review setup requests. Choose their role to grant extra access.</p>
          </div>
        </div>
        <form className="team-invite-form" onSubmit={invite}>
          <label className="modal-field">
            Full name
            <input data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Riya Sharma" />
          </label>
          <label className="modal-field">
            Work email
            <input data-testid="team-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="riya@flowspheresystems.com" />
          </label>
          <label className="modal-field">
            Temporary password
            <input data-testid="team-password-input" type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" />
          </label>
          <label className="modal-field">
            Role
            <select
              data-testid="team-role-select"
              value={permsToPreset(form.perms)}
              onChange={(e) => { const p = PRESETS[e.target.value]; if (p) setForm({ ...form, perms: [...p] }); }}
            >
              <option value="reviewer">Reviewer</option>
              <option value="account_manager">Account Manager</option>
              <option value="full_access">Full Access</option>
              {permsToPreset(form.perms) === "custom" && <option value="custom">Custom</option>}
            </select>
          </label>
          <div className="team-access-toggles" data-testid="team-access-toggles">
            {ACCESS.map((access) => (
              <label key={access.key} className="access-toggle">
                <input type="checkbox" data-testid={`team-perm-${access.key}`} checked={form.perms.includes(access.key)} onChange={() => togglePerm(access.key)} />
                <span><strong>{access.label}</strong><small>{access.hint}</small></span>
              </label>
            ))}
          </div>
          <button className="primary-button team-invite-submit" disabled={busy} data-testid="team-invite-button">
            <UserPlus size={16} /> {busy ? "Adding…" : "Add member"}
          </button>
        </form>
      </div>

      <div className="panel module-panel">
        <div className="module-toolbar">
          <div className="table-search">
            <Users size={16} />
            <span className="table-search-hint">Your master account and internal reviewers</span>
          </div>
        </div>
        <div className="table-wrap">
          {loading ? (
            <div className="admin-loading" data-testid="team-loading">Loading team…</div>
          ) : (
            <table data-testid="team-table">
              <thead>
                <tr><th>Member</th><th>Email</th><th>Role &amp; access</th><th>Added</th><th></th></tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id} data-testid={`team-row-${member.id}`}>
                    <td><strong>{member.name || "—"}</strong></td>
                    <td>{member.email}</td>
                    <td>
                      {member.role === "master"
                        ? <span className="status paid"><ShieldCheck size={12} /> Master · Full access</span>
                        : <span className="status sent">{accessLabel(member.permissions)}</span>}
                    </td>
                    <td>{shortDate(member.created_at)}</td>
                    <td>
                      {member.role !== "master" && (
                        <div className="row-actions">
                          <button className="link-button" data-testid={`team-edit-${member.id}`} onClick={() => setEditing({ ...member, perms: (member.permissions || []).filter((p) => p !== "review_requests") })}>
                            <Sliders size={14} /> Edit access
                          </button>
                          <button className="link-button danger" data-testid={`team-remove-${member.id}`} onClick={() => remove(member)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <EditAccessModal
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function EditAccessModal({ member, onClose, onSaved }) {
  const [perms, setPerms] = useState(member.perms || []);
  const [busy, setBusy] = useState(false);

  const toggle = (key) => setPerms((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/team/members/${member.id}`, { permissions: [...perms, "review_requests"] });
      toast.success("Access updated");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className="create-modal" role="dialog" aria-modal="true" data-testid="team-edit-modal">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">FLOWSPHERE / TEAM ACCESS</p>
            <h2>{member.name || member.email}</h2>
            <p>Choose what this team member can do. They can always review setup requests.</p>
          </div>
          <button className="icon-button" onClick={onClose} data-testid="team-edit-close"><X size={18} /></button>
        </div>
        <div className="team-access-toggles vertical">
          {ACCESS.map((access) => (
            <label key={access.key} className="access-toggle">
              <input type="checkbox" data-testid={`team-edit-perm-${access.key}`} checked={perms.includes(access.key)} onChange={() => toggle(access.key)} />
              <span><strong>{access.label}</strong><small>{access.hint}</small></span>
            </label>
          ))}
        </div>
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy} onClick={save} data-testid="team-edit-save">
            {busy ? "Saving…" : "Save access"}
          </button>
        </div>
      </section>
    </div>
  );
}
